// Standalone Nav + Footer chrome extracted from _v09_directresponse.jsx so
// that interior pages can mount the same header/footer used on the homepage.
import React, { useState, useEffect } from 'react';
import { Link } from '../_shared.jsx';

const theme = {
  bg: '#0b0b0b', fg: '#fafafa', muted: '#a3a3a3',
  border: '#2a2a2a', accent: '#fde047', accentFg: '#0b0b0b',
  inputBg: '#171717', radius: 4,
  font: '"Inter", system-ui, sans-serif',
};

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(max-width: 768px)').matches : true);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener ? mq.addEventListener('change', update) : mq.addListener(update);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', update) : mq.removeListener(update); };
  }, []);
  return isMobile;
};

const HamburgerIcon = ({ open }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    {open ? <><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></> :
            <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>}
  </svg>
);

const eyebrow = {
  fontSize: 12,
  fontWeight: 800,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
};

const defaultNavLinks = [
  { label: 'Solar', href: '/photovoltaik' },
  { label: 'Vorteile', href: '/vorteile' },
  { label: 'Wärmepumpe', href: '/wärmepumpe' },
  { label: 'Förderung', href: '/förderung-finder' },
];

const defaultFooterLinks = [
  { label: 'Impressum', href: '/impressum' },
  { label: 'Datenschutz', href: '/datenschutz' },
  { label: 'AGB', href: '/agb' },
  { label: 'Widerruf', href: '/widerruf' },
  { label: 'Kontakt', href: '/kontakt' },
];

export function V09DirectresponseNav({
  brand = 'Waemo',
  brandMain,
  brandAccent = '',
  brandSuffix = '',
  brandLogo = null,
  navLinks = defaultNavLinks,
  topBarText = 'KfW-Förderung 2026 · bis zu 70 % Zuschuss',
  topBarClock = 'Heute kostenlos starten',
  ctaLabel = 'Jetzt vergleichen',
  ctaHref = '/angebot-vergleichen',
}) {
  const isMobile = useIsMobile();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const resolvedMain = brandMain != null ? brandMain : (brand ? brand.toUpperCase() : 'STROM');
  const resolvedAccent = brandAccent;
  const resolvedSuffix = brandSuffix;
  const brandLabel = brand || 'STROMRETTER24';
  return (
    <>
      <div style={{
        background: theme.accent, color: theme.accentFg,
        padding: isMobile ? '6px 12px' : '8px 32px',
        fontSize: isMobile ? 11 : 12, fontWeight: 700,
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center',
        gap: isMobile ? 2 : 0,
      }}>
        <span>{topBarText}</span>
        <span style={{ fontFamily: '"JetBrains Mono", monospace' }}>{topBarClock}</span>
      </div>
      <div style={{
        padding: isMobile ? '12px 16px' : '14px 32px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderBottom: `1px solid ${theme.border}`,
        background: theme.bg, color: theme.fg, fontFamily: theme.font,
        position: 'relative',
      }}>
        <Link href="/" style={{ display: 'flex', alignItems: 'center', fontWeight: 800, fontSize: isMobile ? 16 : 18, letterSpacing: '-0.02em', color: theme.fg, textDecoration: 'none' }} aria-label={brandLabel + ' Startseite'}>
          {brandLogo
            ? <img src={brandLogo} alt={brandLabel + ' Logo'} width="120" height="32" style={{ height: 32, width: 'auto', maxWidth: 160, display: 'block' }} />
            : <>{resolvedMain}<span style={{ color: theme.accent }}>{resolvedAccent}</span>{resolvedSuffix}</>
          }
        </Link>
        {!isMobile && (
          <>
            <div style={{ display: 'flex', gap: 22, fontSize: 13, color: theme.muted }}>
              {navLinks.map(l => <Link key={l.label} href={l.href} style={{ color: theme.muted, textDecoration: 'none' }}>{l.label}</Link>)}
            </div>
            <a href={ctaHref} style={{ padding: '8px 14px', fontSize: 12, fontWeight: 700, background: theme.accent, color: theme.accentFg, border: 'none', borderRadius: 4, textDecoration: 'none', display: 'inline-block' }}>
              {ctaLabel}
            </a>
          </>
        )}
        {isMobile && (
          <button
            aria-label="Menü"
            onClick={() => setIsMenuOpen(o => !o)}
            style={{ background: 'transparent', border: 'none', color: theme.fg, cursor: 'pointer', padding: 4, display: 'flex' }}
          >
            <HamburgerIcon open={isMenuOpen} />
          </button>
        )}
        {isMobile && isMenuOpen && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0,
            background: theme.bg, borderBottom: `1px solid ${theme.border}`,
            padding: '14px 16px 18px', display: 'flex', flexDirection: 'column', gap: 14,
            zIndex: 50,
          }}>
            {navLinks.map(l => (
              <Link key={l.label} href={l.href} style={{ color: theme.fg, textDecoration: 'none', fontSize: 14 }}>{l.label}</Link>
            ))}
            <a href={ctaHref} style={{
              padding: '10px 14px', fontSize: 13, fontWeight: 700,
              background: theme.accent, color: theme.accentFg, border: 'none', borderRadius: 4,
              textAlign: 'center', textDecoration: 'none', display: 'block', marginTop: 4,
            }}>
              {ctaLabel}
            </a>
          </div>
        )}
      </div>
    </>
  );
}

export function V09DirectresponseFooter({
  brand = 'Waemo',
  brandMain,
  brandAccent = '',
  brandSuffix = '',
  brandLogo = null,
  footerLinks = defaultFooterLinks,
  phoneLabel = '',
  phoneHref = '',
  copyright,
}) {
  const isMobile = useIsMobile();
  const brandLabel = brand || 'STROMRETTER24';
  const resolvedMain = brandMain != null ? brandMain : (brand ? brand.toUpperCase() : 'STROM');
  const resolvedAccent = brandAccent;
  const resolvedSuffix = brandSuffix;
  const resolvedCopyright = copyright != null ? copyright : `© ${new Date().getFullYear()} ${brandLabel}`;
  return (
    <footer style={{
      background: '#000',
      color: theme.accent,
      padding: isMobile ? '24px 16px' : '40px 32px',
      borderTop: `4px solid ${theme.accent}`,
      fontFamily: '"JetBrains Mono", "Inter", monospace',
    }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr 1fr',
        alignItems: isMobile ? 'flex-start' : 'center',
        gap: isMobile ? 18 : 24,
        maxWidth: 1216,
        margin: '0 auto',
      }}>
        <div style={{
          fontWeight: 800,
          fontSize: 14,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: '#fff',
        }}>
          {brandLogo
            ? <img src={brandLogo} alt={brandLabel + ' Logo'} width="120" height="32" style={{ height: 32, width: 'auto', maxWidth: 160, display: 'block' }} />
            : <>{resolvedMain}<span style={{ color: theme.accent }}>-{resolvedAccent}-</span>{resolvedSuffix}</>
          }
        </div>
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: isMobile ? 'flex-start' : 'center',
          gap: isMobile ? 10 : 28,
          fontSize: 12,
          color: theme.accent,
          textTransform: 'uppercase',
          letterSpacing: '0.18em',
          fontWeight: 700,
        }}>
          {footerLinks.map(l => (
            <Link key={l.label} href={l.href} style={{ cursor: 'pointer', color: theme.accent, textDecoration: 'none' }}>{l.label}</Link>
          ))}
        </div>
        {phoneLabel ? (
          <div style={{
            textAlign: isMobile ? 'left' : 'right',
            fontSize: 12,
            color: theme.accent,
            letterSpacing: '0.12em',
            fontWeight: 700,
            textTransform: 'uppercase',
          }}>
            <a href={phoneHref} style={{ color: theme.accent, textDecoration: 'none' }}>{phoneLabel}</a>
          </div>
        ) : null}
      </div>
      <div style={{
        maxWidth: 1216,
        margin: isMobile ? '18px auto 0' : '24px auto 0',
        paddingTop: 20,
        borderTop: `1px solid ${theme.accent}`,
        fontSize: 10,
        color: '#fff',
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        textAlign: 'center',
        fontWeight: 600,
      }}>
        {resolvedCopyright}
      </div>
    </footer>
  );
}
